import { useState } from 'react';
import { Paperclip, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Button, Textarea } from './ui';
import { eventBuilderService, type EventBuilderDraft, type EventBuilderUpload } from '../services/eventBuilderService';

export default function EventBuilderConversation({
  draft,
  pending,
  pendingMessage,
  onSend,
  onRecommendNow,
  onUpload,
}: {
  draft: EventBuilderDraft | null;
  pending: boolean;
  pendingMessage: string | null;
  onSend: (message: string) => Promise<boolean>;
  onRecommendNow: () => void;
  onUpload?: (upload: EventBuilderUpload) => void;
}) {
  const [message, setMessage] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState<EventBuilderUpload | null>(null);

  async function submit() {
    const value = message.trim();
    if (!value || pending) return;
    setMessage('');
    if (!(await onSend(value))) {
      setMessage((current) => current || value);
    }
  }

  async function upload(file: File | undefined) {
    if (!file || !draft || uploading) return;
    setUploading(true);
    try {
      const result = await eventBuilderService.upload(draft.id, file);
      setUploaded(result);
      onUpload?.(result);
    }
    catch (error) { toast.error(error instanceof Error ? error.message : 'Unable to analyze workbook'); }
    finally { setUploading(false); }
  }

  const latestUpload = uploaded ?? draft?.uploads.at(-1);
  const summary = latestUpload?.analysis.summary as Record<string, number> | undefined;

  return <section aria-label="Builder conversation" className="flex min-h-[620px] flex-col rounded-2xl border border-gray-800 bg-gray-900">
    <div className="border-b border-gray-800 p-5">
      <h2 className="font-semibold text-white">Conversation</h2>
      <p className="mt-1 text-xs text-gray-500">Describe it naturally. The builder will ask only for missing decisions.</p>
    </div>
    <div className="flex-1 space-y-3 overflow-y-auto p-5">
      {!draft?.messages.length && <div className="rounded-xl border border-sky-500/20 bg-sky-500/5 p-4 text-sm text-gray-300">
        Try: “A two-day research symposium with posters, sponsors, networking, and attendee feedback.”
      </div>}
      {draft?.messages.map((item) => <div key={item.id} className={`max-w-[90%] whitespace-pre-wrap rounded-xl px-4 py-3 text-sm ${item.role === 'user' ? 'ml-auto bg-sky-500 text-white' : 'bg-gray-950 text-gray-300'}`}>
        {item.content}
      </div>)}
      {pendingMessage && <div className="ml-auto max-w-[90%] rounded-xl bg-sky-500 px-4 py-3 text-sm text-white">{pendingMessage}</div>}
      {pending && <div role="status" aria-label="AI is thinking" className="inline-flex w-fit items-center gap-1.5 rounded-xl bg-gray-950 px-4 py-3">
        <span className="sr-only">AI is thinking</span>
        {[0, 1, 2].map((index) => <span key={index} aria-hidden="true" className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-500" style={{ animationDelay: `${index * 120}ms` }} />)}
      </div>}
    </div>
    <div className="border-t border-gray-800 p-4">
      {draft && <div className="mb-3">
        <label className="inline-flex cursor-pointer items-center gap-2 text-xs text-sky-300"><Paperclip size={14} />{uploading ? 'Analyzing workbook…' : 'Attach schedule or registrants'}<input aria-label="Attach event file" className="sr-only" type="file" accept=".xlsx" disabled={uploading || draft.status === 'converted'} onChange={(event) => void upload(event.target.files?.[0])} /></label>
        {latestUpload && <p className={`mt-2 text-xs ${latestUpload.kind === 'unsupported' ? 'text-amber-300' : 'text-gray-400'}`}>
          {latestUpload.filename} · {latestUpload.kind === 'program' ? `${summary?.sessions ?? 0} sessions, ${summary?.items ?? 0} items` : latestUpload.kind === 'registrants' ? `${summary?.valid_rows ?? 0} valid rows, ${summary?.invalid_rows ?? 0} invalid rows` : (latestUpload.analysis.errors as string[] | undefined)?.join(' · ')}
        </p>}
      </div>}
      <Textarea
        label="Describe your event"
        value={message}
        onChange={(event) => setMessage(event.target.value)}
        placeholder="Tell the builder about your event in your own words..."
        disabled={pending || draft?.status === 'converted'}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            void submit();
          }
        }}
      />
      <div className="mt-3 flex flex-wrap gap-2">
        <Button icon={<Send size={14} />} loading={pending} disabled={!message.trim()} onClick={() => void submit()}>Send</Button>
        {draft?.status === 'active' && draft.template_state.mode === 'discovery' && <Button variant="secondary" disabled={pending} onClick={onRecommendNow}>Recommend now</Button>}
      </div>
    </div>
  </section>;
}
